// MST
import { types, flow, detach, cast } from 'mobx-state-tree'

// Interfaces
import {
  IAssessmentSupportDocumentGetListAttrs,
  IAssessmentSupportDocumentDownloadDocumentAttrs,
} from 'interfaces/back-office/assessment/support-document/support-document-view.interface'
import {
  IAssessmentSupportDocumentApiListResponse,
  IAssessmentSupportDocumentApiDownloadDocumentResponse,
} from 'interfaces/back-office/assessment/support-document/support-document-api.interface'
import { ISharedCommonLoadingAttrs } from 'interfaces/shared/shared-common.interface'

// Extensions
import { withEnvironment } from 'models/extensions/with-environment'
import { withHelper_fileDownload } from 'models/extensions/with-helper'
import { withHelper_commonLoadingModel, withHelper_commonMapLoading } from 'models/extensions/helper'

// Constant
import { LOADING } from 'constant/shared.constant'

// Lodash
import cloneDeep from 'lodash/cloneDeep'

// Types
const { optional, array, model, maybeNull, string, number } = types

// Loading
const { BASE, DOWNLOAD } = LOADING

const documentSupportProps = {
  id: maybeNull(number),
  preconditionTypeName: maybeNull(string),
  userOriginalFileName: maybeNull(string),
  userFileBlobId: maybeNull(string),
  loading: withHelper_commonLoadingModel,
}

// Document Support List
const DocumentSupportList = optional(array(model('DocumentSupportList').props(documentSupportProps)), [])

// Base Model
const AssessmentMyDocumentStoreModel = model('AssessmentSupportDocument')
  .props({
    assessmentSupportDocument_loading: withHelper_commonLoadingModel,
    assessmentSupportDocument_list: DocumentSupportList,
  })
  .actions(self => ({
    assessmentSupportDocument_SET_LOADING: (payload: ISharedCommonLoadingAttrs): void => {
      const map = withHelper_commonMapLoading(self.assessmentSupportDocument_loading, payload)

      detach(self.assessmentSupportDocument_loading)
      self.assessmentSupportDocument_loading = cast(map)
    },
    assessmentSupportDocument_SET_LIST: (payload: IAssessmentSupportDocumentApiListResponse['res']['data']): void => {
      self.assessmentSupportDocument_list = cast(payload)
    },
    assessmentSupportDocument_MAP_LOADING_LIST: (payload: ISharedCommonLoadingAttrs): void => {
      const map = cloneDeep(
        self.assessmentSupportDocument_list.map(list => {
          if (list.userFileBlobId === payload.id) {
            return {
              ...list,
              loading: {
                ...list.loading,
                [payload.type]: payload.value,
              },
            }
          } else {
            return list
          }
        }),
      )

      detach(self.assessmentSupportDocument_list)
      self.assessmentSupportDocument_list = cast(map)
    },
    assessmentSupportDocument_CLEAR_LIST: (): void => {
      self.assessmentSupportDocument_list = cast([])
    },
  }))
  .extend(withEnvironment)
  .actions(self => ({
    /**
     * @description Fetch assessment my document list
     *
     * @param {IAssessmentSupportDocumentGetListAttrs} payload
     *
     * @return {any} any
     */
    assessmentSupportDocument_fetchList: flow(function* (payload: IAssessmentSupportDocumentGetListAttrs) {
      self.assessmentSupportDocument_SET_LOADING({ type: BASE, value: true })

      const response: IAssessmentSupportDocumentApiListResponse =
        yield self.environment.assessmentSupportDocumentApi.assessmentSupportDocument_getList({
          params: { ...payload.params },
          requestConfig: { ...payload.requestConfig },
        })

      if (response.ok) {
        self.assessmentSupportDocument_SET_LIST(response.res.data)

        if (payload?.onSuccess) payload.onSuccess()
      }

      if (!response.ok) {
        if (payload?.onError) payload.onError()
      }

      self.assessmentSupportDocument_SET_LOADING({ type: BASE, value: false })
    }),

    /**
     * @description Download assessment my document, document
     *
     * @param {IAssessmentSupportDocumentDownloadDocumentAttrs} payload
     *
     * @return {any} any
     */
    assessmentSupportDocument_downloadDocument: flow(function* (payload: IAssessmentSupportDocumentDownloadDocumentAttrs) {
      const { blobId: id } = payload.params

      self.assessmentSupportDocument_MAP_LOADING_LIST({ id, type: DOWNLOAD, value: true })

      const response: IAssessmentSupportDocumentApiDownloadDocumentResponse =
        yield self.environment.assessmentSupportDocumentApi.assessmentSupportDocument_downloadDocument({
          params: { ...payload.params },
          requestConfig: { ...payload.requestConfig },
        })

      if (response.ok) {
        withHelper_fileDownload(response.res, response.headers!)

        if (payload?.onSuccess) payload.onSuccess()
      }

      if (!response.ok) {
        if (payload?.onError) payload.onError()
      }

      self.assessmentSupportDocument_MAP_LOADING_LIST({ id, type: DOWNLOAD, value: false })
    }),
  }))

export const createAssessmentSupportDocumentStoreDefaultModel = () => optional(AssessmentMyDocumentStoreModel, {})
